import { Link } from 'react-router-dom';
import { FileText, Calendar, BookOpen, CheckCircle, Globe, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const destinations = [
  { country: 'Ghana', flag: '🇬🇭', region: 'West Africa' },
  { country: 'Bali', flag: '🇮🇩', region: 'Southeast Asia' },
  { country: 'Thailand', flag: '🇹🇭', region: 'Southeast Asia' },
  { country: 'Singapore', flag: '🇸🇬', region: 'Southeast Asia' },
  { country: 'Japan', flag: '🇯🇵', region: 'East Asia' },
  { country: 'Portugal', flag: '🇵🇹', region: 'Europe' },
  { country: 'Morocco', flag: '🇲🇦', region: 'North Africa' },
  { country: 'South Africa', flag: '🇿🇦', region: 'Southern Africa' },
];

export default function Scholarships() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <section className="bg-cabernet py-16 sm:py-20" data-testid="scholarships-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-3">Scholarships</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-cream tracking-tight leading-none mb-4">
            The Global Visionaries Scholarship
          </h1>
          <p className="font-body text-base sm:text-lg text-cream/80 max-w-2xl leading-relaxed">
            Real Estate & Entrepreneurship Essay & Application
          </p>
        </div>
      </section>

      {/* Scholarship Details */}
      <section className="py-16 sm:py-20" data-testid="scholarship-details">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Requirements Card */}
              <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8">
                <h2 className="text-2xl sm:text-3xl font-heading font-medium text-cabernet mb-6">
                  Application Requirements
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-brass mt-0.5 shrink-0" />
                    <div>
                      <p className="font-accent text-sm font-semibold text-ink">Submission Deadline</p>
                      <p className="font-body text-sm text-ink/70">Rolling Admissions - Apply Early</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-brass mt-0.5 shrink-0" />
                    <div>
                      <p className="font-accent text-sm font-semibold text-ink">Format</p>
                      <p className="font-body text-sm text-ink/70">APA Style (7th Edition)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-brass mt-0.5 shrink-0" />
                    <div>
                      <p className="font-accent text-sm font-semibold text-ink">Length</p>
                      <p className="font-body text-sm text-ink/70">Maximum 1 page (excluding Title Page and References)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Essay Prompt */}
              <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8">
                <h3 className="text-xl font-heading font-medium text-cabernet mb-4">The Essay Prompt</h3>
                <div className="bg-cream rounded-md p-6 border-l-4 border-brass">
                  <p className="font-body text-base text-ink/85 leading-relaxed italic">
                    "Describe your long-term career aspirations within the realms of real estate, 
                    business, or international entrepreneurship. How will firsthand exposure to 
                    global markets and international development projects accelerate your professional 
                    growth, and why are you the ideal candidate to represent this organization abroad?"
                  </p>
                </div>
              </div>

              {/* Eligibility */}
              <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8">
                <h3 className="text-xl font-heading font-medium text-cabernet mb-4">Eligibility Criteria</h3>
                <ul className="space-y-3">
                  {[
                    'Currently enrolled high school student (grades 9-12)',
                    'Demonstrated interest in real estate, business, or entrepreneurship',
                    'U.S. citizen or permanent resident',
                    'Commitment to participating in mentorship program upon award',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-brass mt-0.5 shrink-0" />
                      <span className="font-body text-sm text-ink/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-6 sticky top-20">
                <div className="text-center mb-6">
                  <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-2">Award</p>
                  <p className="text-2xl font-heading font-semibold text-cabernet">Fully Funded Experience</p>
                  <p className="font-body text-xs text-ink/60 mt-1">Per scholarship recipient</p>
                </div>
                <div className="border-t border-brass/20 pt-4 mb-6">
                  <p className="font-accent text-xs text-ink/60 uppercase tracking-wider mb-2">Includes</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-brass mt-1.5 shrink-0"></div>
                      <div>
                        <span className="font-body text-xs text-ink/70 font-medium">Scholarship Award</span>
                        <ul className="mt-1 space-y-0.5 pl-3">
                          {[
                            'Flight to and from International Countries',
                            'In and Around Travel',
                            'Hotel Stays',
                            'Cultural Tours',
                            'Conference Events',
                            'Celebratory Dinner',
                          ].map((sub) => (
                            <li key={sub} className="flex items-center gap-1.5">
                              <div className="w-1 h-1 rounded-full bg-brass/50"></div>
                              <span className="font-body text-xs text-ink/60">{sub}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-brass shrink-0"></div>
                      <span className="font-body text-xs text-ink/70">Mentorship Program Access</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-brass shrink-0"></div>
                      <span className="font-body text-xs text-ink/70">Networking Opportunities</span>
                    </li>
                  </ul>
                </div>
                {user ? (
                  <Link to="/dashboard">
                    <Button
                      className="w-full bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm py-5"
                      data-testid="apply-scholarship-button"
                    >
                      Apply Now
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login">
                    <Button
                      className="w-full bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm py-5"
                      data-testid="apply-scholarship-button"
                    >
                      Sign In to Apply
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2027 Scholarship Destinations */}
      <section className="py-16 sm:py-20 bg-card-surface" data-testid="scholarship-destinations">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Globe className="w-5 h-5 text-brass" />
              <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass">Where Will You Go?</p>
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight">
              2027 Scholarship Destinations
            </h2>
            <p className="font-body text-base text-ink/60 mt-3 max-w-xl mx-auto">
              Award recipients gain firsthand exposure to global markets across these international destinations.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {destinations.map((dest, i) => (
              <div
                key={dest.country}
                className="bg-cream rounded-lg border border-brass/20 p-5 text-center hover:border-brass/50 hover:shadow-md transition-all duration-300"
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`destination-${dest.country.toLowerCase().replace(' ', '-')}`}
              >
                <span className="text-3xl block mb-2">{dest.flag}</span>
                <p className="font-heading text-base font-medium text-cabernet">{dest.country}</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <MapPin className="w-3 h-3 text-brass" />
                  <p className="font-accent text-xs text-ink/50">{dest.region}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
